import { useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import { getDisplayErrorMessage } from '@/lib/api';
import { fetchSiteHostOnboarding, isStripeOnboardingUrl, startSiteHostOnboarding, type SiteHostOnboardingView } from '@/lib/siteHostOnboarding';
import { AuthLayout } from '@/components/AuthLayout';
import formStyles from '@/components/AuthForm.module.css';
import styles from './MagicLinkVerifyPage.module.css';

interface Props {
  onNavigate: (path: string) => void;
}

/**
 * A site host's Stripe onboarding invite (Warren, 29 October 2026, The Novelle: "Generate its Stripe onboarding link and give
 * it to me privately so I can email it from my Gmail"): /site-host-onboarding?token=..., no sign-in (hoisted above the auth
 * guard like the alert choice and Done pages). Opening the page only reads, so a mail scanner previewing the email spends
 * nothing. The one button asks the Api for a fresh Stripe onboarding link, made at that moment, and opens it; Stripe sends
 * the browser back here when it is done or its link has lapsed (?returned=1), and the button makes another.
 */
export function SiteHostOnboardingPage({ onNavigate }: Props) {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token') ?? '', returned = params.get('returned') === '1';
  const [view, setView] = useState<SiteHostOnboardingView | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [complete, setComplete] = useState(false);

  useEffect(() => {
    if (!token) { setError('This link is incomplete. Open it from the email again.'); return; }
    fetchSiteHostOnboarding(token)
      .then(setView)
      .catch(async (err: unknown) => setError(await getDisplayErrorMessage(err, 'This link is no longer valid.')));
  }, [token]);

  const press = async (): Promise<void> => {
    setBusy(true);
    setError('');
    try {
      const result = await startSiteHostOnboarding(token);
      if (result.state === 'complete') { setComplete(true); return; }
      if (!isStripeOnboardingUrl(result.url)) throw new Error('Stripe could not be opened. Try again in a minute.');
      window.location.assign(result.url);
    } catch (err) {
      setError(await getDisplayErrorMessage(err, 'Stripe could not be opened. Try again in a minute.'));
    } finally { setBusy(false); }
  };

  const until = view ? new Date(view.expires_at).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }) : '';
  return (
    <AuthLayout title={view?.location_name || 'Electricity reimbursement'} subtitle="Bank account for electricity reimbursements">
      {!view && !error && <div className={styles.spinnerWrap}><Loader2 size={36} className={styles.spinner} /></div>}
      {error && (
        <div className={styles.errorStack}>
          <div className={formStyles.serverError} role="alert">
            <AlertTriangle size={16} style={{ verticalAlign: 'middle', marginRight: 8 }} />
            {error}
          </div>
          {!view && <button type="button" className={formStyles.btnSecondary} onClick={() => onNavigate('/login')}>Go to sign in</button>}
        </div>
      )}
      {view && (complete ? (
        <p role="status"><CheckCircle2 size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
          This bank account is set up and Stripe shows payouts enabled. Nothing more to do, thank you.</p>
      ) : (
        <div className={styles.errorStack}>
          {returned
            ? <p role="status">Thank you. If Stripe said anything is still needed, continue to Stripe again from here.</p>
            : <p>EV Initiative reimburses this site&apos;s electricity through Stripe. Set up the company bank account it is paid to:
              Stripe asks for the company&apos;s details and its bank account, and EV Initiative never sees the bank number.</p>}
          <button type="button" className={formStyles.submitBtn} disabled={busy} onClick={() => { void press(); }}>
            {busy ? 'Opening Stripe...' : 'Continue to Stripe'}
          </button>
          <p>This link works until {until}. Questions: <a href="mailto:support@evinitiative.com">support@evinitiative.com</a></p>
        </div>
      ))}
    </AuthLayout>
  );
}

export default SiteHostOnboardingPage;
